import { Component, OnInit } from '@angular/core';
import { FormGroup, FormArray, FormBuilder, Validators } from '@angular/forms';
import { BudgetCategoryService } from '../../../services/budget-category.service';
import { ProjectService } from '../../../services/project.service';
import { ToastService } from '../../../services/toast.service';
import { LoadingService } from '../../../services/loading.service';
import { CurrencyService, CurrencyResponseDTO } from '../../../services/currency.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-new-project',
  templateUrl: './new-project.component.html',
  styleUrl: './new-project.component.scss'
})
export class NewProjectComponent implements OnInit {

  projectForm!: FormGroup;
  projectFormArray!: FormArray;
  currentStep = 1;
  totalSteps = 3;
  categories: any[] = [];
  subcategories: any = {};
  selectedTabInStep3: any = 'Ingresos';
  showPreview: boolean = false;
  projectIdExists: boolean = false;

  // Currencies dinámicas desde API
  currencies: CurrencyResponseDTO[] = [];
  baseCurrency: CurrencyResponseDTO | null = null;
  loadingCurrencies: boolean = false;

  projectTypes = [
    { value: 1, label: 'Diploma' },
    { value: 2, label: 'Seminario' },
    { value: 3, label: 'Especialización' },
    { value: 4, label: 'Consultoria' },
    { value: 5, label: 'Pre-grado' },
    { value: 6, label: 'Postgrado' },
    { value: 7, label: 'Investigación' },
    { value: 8, label: 'Bienestar' },
    { value: 9, label: 'Otro' },
  ];

  projectStatus = [
    { value: 1, label: 'Creado' },
    { value: 2, label: 'Cerrado' },
    { value: 3, label: 'Precierre' },
    { value: 4, label: 'Inactivo' },
    { value: 5, label: 'Cancelado' },
  ];

  months = [
    { value: 'ENE', label: 'Enero' },
    { value: 'FEB', label: 'Febrero' },
    { value: 'MAR', label: 'Marzo' },
    { value: 'ABR', label: 'Abril' },
    { value: 'MAY', label: 'Mayo' },
    { value: 'JUN', label: 'Junio' },
    { value: 'JUL', label: 'Julio' },
    { value: 'AGO', label: 'Agosto' },
    { value: 'SEP', label: 'Septiembre' },
    { value: 'OCT', label: 'Octubre' },
    { value: 'NOV', label: 'Noviembre' },
    { value: 'DIC', label: 'Diciembre' },
  ]
  constructor(
    private fb: FormBuilder,
    private router: Router,
    private _budgetCategoryService: BudgetCategoryService,
    private _projectService: ProjectService,
    private toastService: ToastService,
    private _loadingService: LoadingService,
    private _currencyService: CurrencyService
  ) {
    this.projectForm = this.fb.group({
      projectId: ['', Validators.required],
      name: ['', Validators.required],
      inscriptionAmountCOP: ['', Validators.required],
      description: [''],
      period: ['', Validators.required],
      status: [1, Validators.required], // CREATED por defecto
      date: [''],
      dateClosed: [''],
      typeProject: [1, Validators.required], // DIPLOMA por defecto
      duration: ['', [Validators.required, Validators.min(1)]],
      noParticipants: [0],
      projectedParticipants: ['', [Validators.required, Validators.min(0)]],
      observations: [''],
      projectedRevenue: ['', [Validators.required, Validators.min(0)]],
      projectedSpend: ['', [Validators.required, Validators.min(0)]],
      totalIncome: [0],
      totalExpenses: [0],
      budgets: this.fb.array([this.createItemFormGroup()]),

      // Campos para inscripciones (se configurarán dinámicamente)
      currencyId: [null, Validators.required], // Se asignará cuando se cargue la moneda base
      inscriptionAmountUSD: ['', [Validators.required, Validators.min(0.01)]],
      currencyCode: ['']  // Se asignará dinámicamente
    });
  }



  ngOnInit(): void {
    this.setupConditionalValidations();
    this.getCategories();
    this.loadCurrencies();
  }

  setupConditionalValidations(): void {
    this.projectForm.get('date')?.valueChanges.subscribe((startDate) => {
      const dateClosedControl = this.projectForm.get('dateClosed');
      if (startDate) {
        dateClosedControl?.setValidators([Validators.required]);
      } else {
        dateClosedControl?.clearValidators();
      }
      dateClosedControl?.updateValueAndValidity();
    });
  }



  closePreview(): void {
    this.showPreview = false;
  }

  openPreview() {
    this.showPreview = true;
  }

  isCurrentStepValid(): boolean | undefined {
    switch (this.currentStep) {
      case 1:
        const baseValidation = this.projectForm.get('name')?.valid &&
          this.projectForm.get('projectId')?.valid &&
          this.projectForm.get('currencyId')?.valid &&
          this.projectForm.get('period')?.valid &&
          this.projectForm.get('status')?.valid &&
          this.projectForm.get('date')?.valid &&
          this.projectForm.get('dateClosed')?.valid &&
          this.projectForm.get('typeProject')?.valid &&
          this.projectForm.get('duration')?.valid &&
          this.projectForm.get('projectedParticipants')?.valid;

        // Validamos ambos campos como obligatorios
        return baseValidation &&
          this.projectForm.get('inscriptionAmountCOP')?.valid &&
          this.projectForm.get('inscriptionAmountUSD')?.valid;
      case 2:
        return this.projectForm.get('projectedRevenue')?.valid &&
          this.projectForm.get('projectedSpend')?.valid;
      case 3:
        return this.isBudgetsArrayValid();
      default:
        return true; // El paso final no requiere validación adicional
    }
  }

  private isBudgetsArrayValid(): boolean {
    const budgetsArray = this.projectForm.get('budgets') as FormArray;
    return budgetsArray.controls.every((item) => item.valid);
  }

  createItemFormGroup(): FormGroup {
    const itemForm = this.fb.group({
      budgetCategoryId: ['', Validators.required],
      budgetSubcategoryId: ['', Validators.required],
      qty: [1, Validators.min(1)],
      projectedAmount: ['', Validators.min(0)]
    });
    return itemForm;
  }

  loadProjectedAmount(data: any, index: number) {
    const { value: subcategoryId } = data.target;
    const subcategory = this.findSubcategory(subcategoryId);
    if (subcategory) {
      let itemsArray = this.projectForm.get('budgets') as FormArray;
      let itemGroup = itemsArray.at(index) as FormGroup;
      itemGroup.get('projectedAmount')?.setValue(subcategory.amount);
    }
  }

  findSubcategory(subcategoryId: number) {
    for (const categoryId in this.subcategories) {
      if (this.subcategories.hasOwnProperty(categoryId)) {
        const subcategories = this.subcategories[categoryId];
        const subcategory = subcategories.find((sub: any) => sub.id == subcategoryId);
        if (subcategory) {
          return subcategory;
        }
      }
    }
    return null;
  }

  nextStep(): void {
    if (this.isCurrentStepValid()) {
      this.currentStep++;
    } else {
      this.projectForm.markAllAsTouched(); // Muestra los errores en los campos inválidos
    }
  }

  prevStep() {
    this.currentStep--;
  }

  get budgets() {
    return this.projectForm.controls["budgets"] as FormArray;
  }

  addItem() {
    this.budgets.push(this.createItemFormGroup());
  }

  removeItem(index: number) {
    this.budgets.removeAt(index);
  }

  loadCurrencies(): void {
    this.loadingCurrencies = true;

    // Cargar monedas activas y moneda base en paralelo
    Promise.all([
      this._currencyService.getActiveCurrencies().toPromise(),
      this._currencyService.getBaseCurrency().toPromise()
    ]).then(([currenciesResult, baseCurrencyResult]) => {
      this.loadingCurrencies = false;

      if (currenciesResult?.isSuccess && currenciesResult.data) {
        this.currencies = currenciesResult.data;
      }

      if (baseCurrencyResult?.isSuccess && baseCurrencyResult.data) {
        this.baseCurrency = baseCurrencyResult.data;

        // Configurar valores por defecto del form con moneda base
        this.projectForm.patchValue({
          currencyId: this.baseCurrency.id,
          currencyCode: this.baseCurrency.code
        });
      }

    }).catch((error) => {
      this.loadingCurrencies = false;
      console.error('Error loading currencies:', error);
      this.toastService.showToast('error', 'Error cargando monedas del sistema');
    });
  }

  getCategories() {
    this._budgetCategoryService.getAll().subscribe({
      next: (data) => {
        // Manejar estructura paginada: data puede ser array directo o {data: [...], totalCount: ...}
        const rawData = data?.data;
        this.categories = Array.isArray(rawData) ? rawData : (rawData?.data || []);
        this.categories.forEach((category: any) => {
          this.subcategories[category.id] = category.budgetSubcategories;
        });
      },
      error: (error) => {
        console.error(error)
      }
    })
  }

  getSubcategories(categoryId: number): any[] {
    return this.subcategories[categoryId] || [];
  }

  getBudgetTypeName(budgetType: string): string {
    switch (budgetType) {
      case 'INCOME':
        return 'Ingresos';
      case 'EXPENSE':
        return 'Gastos';
      default:
        return 'Desconocido';
    }
  }

  /**
   * Obtiene la información de una moneda por su ID
   */
  getCurrencyById(id: number): CurrencyResponseDTO | null {
    return this.currencies.find(c => c.id === id) || null;
  }

  /**
   * Obtiene el nombre para mostrar de una moneda
   */
  getCurrencyDisplayName(currency: CurrencyResponseDTO): string {
    return `${currency.name} (${currency.code})`;
  }

  onSubmit() {
    this._loadingService.show();
    console.log("before", this.projectForm.value)
    const newProject = {
      ...this.projectForm.value,
      status: Number(this.projectForm.value.status),
      typeProject: Number(this.projectForm.value.typeProject),
      budgets: this.projectForm.value.budgets.map((data: any, index: number) => ({
        ...data,
        budgetCategoryId: Number((this.budgets.at(index) as FormGroup).get('budgetCategoryId')?.value),
        budgetSubcategoryId: Number((this.budgets.at(index) as FormGroup).get('budgetSubcategoryId')?.value)
      }))
    };
    this._projectService.insert(newProject).subscribe({
      next: (response: any) => {
        this.handleSuccess('Proyecto creado correctamente.');
      },
      error: (err) => {
        this.handleError('No se pudo crear el proyecto.');
      }
    })
    console.log("after", newProject)
  }

  checkDuplicity($event: Event) {
    const nemonico = this.projectForm.get('projectId')?.value;
    if (nemonico != null) {
      this._projectService.checkDuplicity(nemonico).subscribe({
        next: (response: any) => {
          this.projectIdExists = response
        },
        error: (err) => {

        }
      })
    }

  }


  private handleSuccess(message: string): void {
    this._loadingService.hide();
    this.toastService.showToast('success', message);
    this.router.navigate(['/projects']);
  }

  private handleError(message: string): void {
    this._loadingService.hide();
    this.toastService.showToast('error', message);
  }


}
